import type { Notification, FocusMode, NotificationChannel } from '@/types/notifications';
import { shouldDeliverNotification, type FilterResult } from '@/lib/focusModeFilters';
import { supabase } from '@/integrations/supabase/client';

/**
 * Focus Mode Notification Service
 * 
 * This service acts as a middleware layer between the notification delivery service
 * and the notification manager. It applies focus mode filtering without modifying
 * the core delivery infrastructure.
 * 
 * **Integration Pattern:**
 * ```typescript
 * // In NotificationManager or delivery handler:
 * const handler = (notification) => {
 *   const filterResult = focusModeNotificationService.shouldDeliver(notification);
 *   if (!filterResult.allowed) {
 *     // Skip delivery and log as blocked
 *     return;
 *   }
 *   // Proceed with normal delivery
 * };
 * ```
 */

class FocusModeNotificationService {
  private activeFocusMode: FocusMode | null = null;
  private channels: NotificationChannel[] = [];
  private channelsLoaded = false;

  /**
   * Set the currently active focus mode
   * Call this whenever focus mode changes
   */
  setActiveFocusMode(focusMode: FocusMode | null): void {
    this.activeFocusMode = focusMode;
    console.log('[FocusModeService] Active mode updated:', focusMode?.name || 'None');
  }

  /**
   * Get the current active focus mode
   */
  getActiveFocusMode(): FocusMode | null {
    return this.activeFocusMode;
  }

  /**
   * Load available channels from Supabase
   * Should be called once at initialization
   */
  async loadChannels(organizationId: string): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('notification_channels')
        .select('*')
        .or(`organization_id.eq.${organizationId},organization_id.is.null`)
        .eq('enabled', true);

      if (error) throw error;

      this.channels = (data || []) as NotificationChannel[];
      this.channelsLoaded = true;
      console.log('[FocusModeService] Loaded', this.channels.length, 'channels');
    } catch (error) {
      console.error('[FocusModeService] Failed to load channels:', error);
      this.channels = [];
    }
  }

  /**
   * Refresh channel list
   */
  async refreshChannels(organizationId: string): Promise<void> {
    await this.loadChannels(organizationId);
  }

  /**
   * Check if a notification should be delivered based on active focus mode
   * This is the main integration point with the delivery system
   */
  shouldDeliver(notification: Notification): FilterResult {
    // No focus mode active = allow all
    if (!this.activeFocusMode) {
      return { allowed: true };
    }

    // Find the channel for this notification
    const channel = this.channels.find(c => c.id === notification.channel_id);

    // Apply focus mode filtering
    return shouldDeliverNotification(notification, this.activeFocusMode, channel);
  }

  /**
   * Log a blocked notification to history
   * Call this when a notification is blocked by focus mode
   */
  async logBlockedNotification(
    notification: Notification,
    filterResult: FilterResult
  ): Promise<void> {
    if (!this.activeFocusMode || filterResult.allowed) {
      return; // Nothing to log
    }

    try {
      await supabase.from('notification_history').insert({
        notification_id: notification.id,
        user_id: notification.user_id,
        organization_id: notification.organization_id,
        action: 'blocked',
        action_data: {
          reason: filterResult.reason,
          block_reason: filterResult.blockReason,
          focus_mode_name: this.activeFocusMode.name,
        },
        focus_mode_active: true,
        focus_mode_id: this.activeFocusMode.id,
      });

      console.log('[FocusModeService] Logged blocked notification:', {
        notification_id: notification.id,
        reason: filterResult.reason,
        focus_mode: this.activeFocusMode.name,
      });
    } catch (error) {
      console.error('[FocusModeService] Failed to log blocked notification:', error);
    }
  }

  /**
   * Get statistics about blocked notifications
   */
  async getBlockedStats(userId: string, since?: Date): Promise<{
    totalBlocked: number;
    blocksByMode: Record<string, number>;
    blocksByReason: Record<string, number>;
  }> {
    try {
      const query = supabase
        .from('notification_history')
        .select('*')
        .eq('user_id', userId)
        .eq('focus_mode_active', true)
        .eq('action', 'blocked');

      if (since) {
        query.gte('created_at', since.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      const stats = {
        totalBlocked: data?.length || 0,
        blocksByMode: {} as Record<string, number>,
        blocksByReason: {} as Record<string, number>,
      };

      data?.forEach((entry) => {
        // Count by mode
        const modeName = entry.action_data?.focus_mode_name || 'Unknown';
        stats.blocksByMode[modeName] = (stats.blocksByMode[modeName] || 0) + 1;

        // Count by reason
        const reason = entry.action_data?.block_reason || 'other';
        stats.blocksByReason[reason] = (stats.blocksByReason[reason] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('[FocusModeService] Failed to get blocked stats:', error);
      return {
        totalBlocked: 0,
        blocksByMode: {},
        blocksByReason: {},
      };
    }
  }

  /**
   * Get recently blocked notifications
   */
  async getBlockedNotifications(
    userId: string,
    limit: number = 50
  ): Promise<Array<{
    notification_id: string;
    blocked_at: string;
    reason: string;
    focus_mode_name: string;
  }>> {
    try {
      const { data, error } = await supabase
        .from('notification_history')
        .select('*')
        .eq('user_id', userId)
        .eq('focus_mode_active', true)
        .eq('action', 'blocked')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((entry) => ({
        notification_id: entry.notification_id,
        blocked_at: entry.created_at,
        reason: entry.action_data?.reason || 'Unknown',
        focus_mode_name: entry.action_data?.focus_mode_name || 'Unknown Mode',
      }));
    } catch (error) {
      console.error('[FocusModeService] Failed to get blocked notifications:', error);
      return [];
    }
  }

  /**
   * Clear service state (for cleanup)
   */
  reset(): void {
    this.activeFocusMode = null;
    this.channels = [];
    this.channelsLoaded = false;
  }
}

// Export singleton instance
export const focusModeNotificationService = new FocusModeNotificationService();
